import { forwardRef, Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Transition } from '@headlessui/react';
import { audioManager } from 'lib/audio/audio-manager';
import { AUDIO_TYPES } from 'lib/audio/audio.types';

import { NFT_IMAGES, TierKey } from 'modules/nfts/constants';
import { tierIdToTierKey } from 'modules/nfts/utils/utils';

import { ImageData, OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { InfiniteMaxProgressBar } from 'shared/components/ui/progress-bar/progress-bar';
import { MovesEnum, RoundOutcome } from 'shared/constants/shared-enums';
import { useQuestsStore } from 'shared/store/questsStore';

import mePaperLose from 'assets/moves/me-paper-lost.png?format=webp;avif&as=picture';
import mePaperWin from 'assets/moves/me-paper-win.png?format=webp;avif&as=picture';
import meRockLose from 'assets/moves/me-rock-lost.png?format=webp;avif&as=picture';
import meRockWin from 'assets/moves/me-rock-win.png?format=webp;avif&as=picture';
import meScissorsLose from 'assets/moves/me-scissors-lost.png?format=webp;avif&as=picture';
import meScissorsWin from 'assets/moves/me-scissors-win.png?format=webp;avif&as=picture';
import opponentPaperLose from 'assets/moves/opponent-paper-lost.png?format=webp;avif&as=picture';
import opponentPaperWin from 'assets/moves/opponent-paper-win.png?format=webp;avif&as=picture';
import opponentPaper from 'assets/moves/opponent-paper.png?format=webp;avif&as=picture';
import opponentRockLose from 'assets/moves/opponent-rock-lost.png?format=webp;avif&as=picture';
import opponentRockWin from 'assets/moves/opponent-rock-win.png?format=webp;avif&as=picture';
import opponentRock from 'assets/moves/opponent-rock.png?format=webp;avif&as=picture';
import opponentScissorsLose from 'assets/moves/opponent-scissors-lost.png?format=webp;avif&as=picture';
import opponentScissorsWin from 'assets/moves/opponent-scissors-win.png?format=webp;avif&as=picture';
import opponentScissors from 'assets/moves/opponent-scissors.png?format=webp;avif&as=picture';

export type Tiers = keyof typeof NFT_IMAGES;

export const DISPLAY_PROGRESS_TIMER_DELAY = 2500;
export const DISPLAY_POINTS_DELAY = 3250;

interface MobileRoundAnimationProps {
  pointBalance: number;
  prevPoints: number;
  score: {
    first: number;
    second: number;
  };
  cards: {
    myMove: MovesEnum;
    opponentMove: MovesEnum;
  };
  lastRoundOutcome: RoundOutcome;
  setShowRoundAnimation: (flag: boolean) => void;
  textForWin?: string;
  textForLose?: string;
  textForDraw?: string;
}

interface Card {
  card: ImageData;
  win: ImageData;
  lose: ImageData;
}

export const MobileRoundAnimation = forwardRef<HTMLDivElement, MobileRoundAnimationProps>(
  (
    {
      pointBalance,
      prevPoints,
      score,
      lastRoundOutcome,
      setShowRoundAnimation,
      cards,
      textForWin,
      textForLose,
      textForDraw,
    },
    ref,
  ) => {
    const [firstCardVisible, setFirstCardVisible] = useState(false);
    const [secondCardVisible, setSecondCardVisible] = useState(false);
    const [timerVisible, setTimerVisible] = useState(false);
    const [roundWonVisible, setRoundWonVisible] = useState(false);
    const [showResultCards, setShowResultCards] = useState(false);
    const [myCard, setMyCard] = useState<Card | null>(null);
    const [opponentCard, setOpponentCard] = useState<Card | null>(null);
    const [showProgress, setShowProgress] = useState(false);
    const [displayedPoints, setDisplayedPoints] = useState(prevPoints);
    const { t } = useTranslation();
    const { bestTierPerMove, bestOpponentTierPerMove } = useQuestsStore();

    const myBestRockKey: TierKey = tierIdToTierKey(bestTierPerMove?.[MovesEnum.Rock] ?? null);
    const myBestPaperKey: TierKey = tierIdToTierKey(bestTierPerMove?.[MovesEnum.Paper] ?? null);
    const myBestScissorsKey: TierKey = tierIdToTierKey(
      bestTierPerMove?.[MovesEnum.Scissors] ?? null,
    );

    const opponentBestRockKey: TierKey = tierIdToTierKey(
      bestOpponentTierPerMove?.[MovesEnum.Rock] ?? null,
    );
    const opponentBestPaperKey: TierKey = tierIdToTierKey(
      bestOpponentTierPerMove?.[MovesEnum.Paper] ?? null,
    );
    const opponentBestScissorsKey: TierKey = tierIdToTierKey(
      bestOpponentTierPerMove?.[MovesEnum.Scissors] ?? null,
    );

    useEffect(() => {
      if (pointBalance > prevPoints) {
        setShowProgress(false);
        setDisplayedPoints(prevPoints);

        const progressTimer = setTimeout(() => {
          setShowProgress(true);
        }, DISPLAY_PROGRESS_TIMER_DELAY);

        const pointsTimer = setTimeout(() => {
          setDisplayedPoints(pointBalance);
        }, DISPLAY_POINTS_DELAY);

        return () => {
          clearTimeout(pointsTimer);
          clearTimeout(progressTimer);
        };
      } else {
        setShowProgress(true);
        setDisplayedPoints(pointBalance);
      }
    }, [prevPoints, pointBalance]);

    useEffect(() => {
      if (cards.myMove === MovesEnum.Paper) {
        setMyCard({
          card: NFT_IMAGES[myBestPaperKey][MovesEnum.Paper],
          win: myBestPaperKey === 'none' ? mePaperWin : NFT_IMAGES[myBestPaperKey][MovesEnum.Paper],
          lose:
            myBestPaperKey === 'none' ? mePaperLose : NFT_IMAGES[myBestPaperKey][MovesEnum.Paper],
        });
      } else if (cards.myMove === MovesEnum.Scissors) {
        setMyCard({
          card: NFT_IMAGES[myBestScissorsKey][MovesEnum.Scissors],
          win:
            myBestScissorsKey === 'none'
              ? meScissorsWin
              : NFT_IMAGES[myBestScissorsKey][MovesEnum.Scissors],
          lose:
            myBestScissorsKey === 'none'
              ? meScissorsLose
              : NFT_IMAGES[myBestScissorsKey][MovesEnum.Scissors],
        });
      } else if (cards.myMove === MovesEnum.Rock) {
        setMyCard({
          card: NFT_IMAGES[myBestRockKey][MovesEnum.Rock],
          win: myBestRockKey === 'none' ? meRockWin : NFT_IMAGES[myBestRockKey][MovesEnum.Rock],
          lose: myBestRockKey === 'none' ? meRockLose : NFT_IMAGES[myBestRockKey][MovesEnum.Rock],
        });
      }
      if (cards.opponentMove === MovesEnum.Paper) {
        setOpponentCard({
          card:
            opponentBestPaperKey === 'none'
              ? opponentPaper
              : NFT_IMAGES[opponentBestPaperKey][MovesEnum.Paper],
          win:
            opponentBestPaperKey === 'none'
              ? opponentPaperWin
              : NFT_IMAGES[opponentBestPaperKey][MovesEnum.Paper],
          lose:
            opponentBestPaperKey === 'none'
              ? opponentPaperLose
              : NFT_IMAGES[opponentBestPaperKey][MovesEnum.Paper],
        });
      } else if (cards.opponentMove === MovesEnum.Scissors) {
        setOpponentCard({
          card:
            opponentBestScissorsKey === 'none'
              ? opponentScissors
              : NFT_IMAGES[opponentBestScissorsKey][MovesEnum.Scissors],
          win:
            opponentBestScissorsKey === 'none'
              ? opponentScissorsWin
              : NFT_IMAGES[opponentBestScissorsKey][MovesEnum.Scissors],
          lose:
            opponentBestScissorsKey === 'none'
              ? opponentScissorsLose
              : NFT_IMAGES[opponentBestScissorsKey][MovesEnum.Scissors],
        });
      } else if (cards.opponentMove === MovesEnum.Rock) {
        setOpponentCard({
          card:
            opponentBestRockKey === 'none'
              ? opponentRock
              : NFT_IMAGES[opponentBestRockKey][MovesEnum.Rock],
          win:
            opponentBestRockKey === 'none'
              ? opponentRockWin
              : NFT_IMAGES[opponentBestRockKey][MovesEnum.Rock],
          lose:
            opponentBestRockKey === 'none'
              ? opponentRockLose
              : NFT_IMAGES[opponentBestRockKey][MovesEnum.Rock],
        });
      }
    }, [cards.myMove]);
    useEffect(() => {
      if (lastRoundOutcome === RoundOutcome.Win) {
        // Win animation: first card from right, then second from left
        // 300ms delay, then first card (300ms), 2s delay, second card (300ms), middle (300ms), 1s delay, text, 1s delay, hide
        const timer1 = setTimeout(() => {
          setFirstCardVisible(true);
        }, 500); // Start after 500ms delay

        const timer2 = setTimeout(() => {
          setSecondCardVisible(true);
        }, 2800); // 500 + 300 + 2000 = 2800ms

        const timer3 = setTimeout(() => {
          setTimerVisible(true);
          setShowResultCards(true);
        }, 5100); // 2800 + 300 (card animation) + 2000 (delay) = 5100ms

        const timer4 = setTimeout(() => {
          setRoundWonVisible(true);
        }, 6400); // 5100 + 1000 + 300 = 6400ms

        const timer5 = setTimeout(() => {
          setShowRoundAnimation(false);
        }, 8900); // 6400 + 2500 = 7600ms

        return () => {
          clearTimeout(timer1);
          clearTimeout(timer2);
          clearTimeout(timer3);
          clearTimeout(timer4);
          clearTimeout(timer5);
        };
      } else {
        // Lose animation: second card from left first, then first from right
        // 300ms delay, then second card (300ms), 2s delay, first card (300ms), middle (300ms), 1s delay, text, 1s delay, hide
        const timer1 = setTimeout(() => {
          setSecondCardVisible(true);
        }, 500);

        const timer2 = setTimeout(() => {
          setFirstCardVisible(true);
        }, 2800);

        const timer3 = setTimeout(() => {
          setTimerVisible(true);
          setShowResultCards(true);
        }, 5100);
        const timer4 = setTimeout(() => {
          setRoundWonVisible(true);
        }, 6100);

        const timer5 = setTimeout(() => {
          setShowRoundAnimation(false);
        }, 8600);

        return () => {
          clearTimeout(timer1);
          clearTimeout(timer2);
          clearTimeout(timer3);
          clearTimeout(timer4);
          clearTimeout(timer5);
        };
      }
    }, [lastRoundOutcome]);

    useEffect(() => {
      const outcomeSoundMap: Partial<Record<RoundOutcome, AUDIO_TYPES>> = {
        [RoundOutcome.Win]: AUDIO_TYPES.ROUND_WIN,
        [RoundOutcome.Lose]: AUDIO_TYPES.ROUND_LOSS,
        [RoundOutcome.Draw]: AUDIO_TYPES.DRAW,
      };

      const soundType = outcomeSoundMap[lastRoundOutcome];

      if (soundType) {
        const timer = setTimeout(() => {
          audioManager.playSound(soundType);
        }, 6200);

        return () => clearTimeout(timer);
      }
    }, [lastRoundOutcome]);

    if (lastRoundOutcome === RoundOutcome.Win)
      return (
        <div
          ref={ref}
          className="relative left-0 top-0 z-50 flex h-screen flex-col items-center justify-center overflow-hidden py-4"
        >
          <OptimizedImage
            imageData={showResultCards ? opponentCard?.lose : opponentCard?.card}
            alt="card back"
            className={`h-[70vh] max-h-[390px] w-auto -rotate-[15deg] transition-opacity transition-transform duration-300 ease-out ${firstCardVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'} `}
          />

          <Transition
            show={timerVisible}
            enter="transition-opacity duration-300 ease-out"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            as={Fragment}
          >
            <div
              className="absolute z-40 flex h-[50%] w-screen flex-col items-center justify-center transition-opacity duration-300 ease-out"
              style={{
                background:
                  'linear-gradient(180deg, rgba(6, 4, 54, 0) 0%, rgba(6, 4, 54, 0.8) 24.84%, #060436 50.43%, rgba(6, 4, 54, 0.8) 77.4%, rgba(6, 4, 54, 0) 100%)',
              }}
            >
              <div className="text-[128px] text-white">
                {' '}
                <span
                  className="min-w-[66px]"
                  style={{
                    background: 'linear-gradient(180deg, #FFFFFF 0%, #FF8B00 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {roundWonVisible ? score.first : score.first - 1}
                </span>{' '}
                <span
                  style={{
                    background:
                      'linear-gradient(180deg, rgba(255, 255, 255, 1) 0%, rgba(79, 181, 255, 1) 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  :{' '}
                  <span
                    className="min-w-[66px]"
                    style={{
                      background:
                        'linear-gradient(180deg, rgba(255, 255, 255, 1) 0%, rgba(79, 181, 255, 1) 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {score.second}
                  </span>
                </span>
              </div>

              <p
                className="flex items-center justify-center text-center text-[32px] font-bold leading-[40px] transition-opacity duration-500 ease-out"
                style={{
                  background: 'linear-gradient(180deg, #FFFFFF 0%, #FFE184 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  flex: 'none',
                  order: 1,
                  flexGrow: 0,
                  opacity: roundWonVisible ? 1 : 0,
                }}
              >
                <span>{textForWin || t('cardAnimationRoundTexts.win')}</span>
              </p>
            </div>
          </Transition>

          <OptimizedImage
            imageData={showResultCards ? myCard?.win : myCard?.card}
            alt="card front"
            className={`h-[70vh] max-h-[390px] w-auto rotate-[15deg] transition-opacity transition-transform duration-300 ease-out ${secondCardVisible ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'} `}
            style={{
              marginTop: 'max(-21vh, -117px)', // 30% overlap: use smaller of 21vh or 117px
            }}
          />

          <Transition
            as={Fragment}
            show={showProgress}
            enter="transition-opacity duration-300 ease-out"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="absolute bottom-10 w-[90%] max-w-72">
              <InfiniteMaxProgressBar value={Number(displayedPoints)} showAttention />
            </div>
          </Transition>
        </div>
      );

    return (
      <div
        ref={ref}
        className="relative left-0 top-0 z-50 flex h-screen flex-col items-center justify-center overflow-hidden py-4"
      >
        <OptimizedImage
          imageData={
            showResultCards && lastRoundOutcome !== RoundOutcome.Draw
              ? opponentCard?.win
              : opponentCard?.card
          }
          alt="card back"
          className={`z-10 h-[70vh] max-h-[390px] w-auto -rotate-[15deg] transition-opacity transition-transform duration-300 ease-out ${firstCardVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'} `}
        />
        <Transition
          show={timerVisible}
          enter="transition-opacity duration-300 ease-out"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          as={Fragment}
        >
          <div
            className="absolute z-40 flex h-[50%] w-screen flex-col items-center justify-center transition-opacity duration-300 ease-out"
            style={{
              background:
                'linear-gradient(180deg, rgba(6, 4, 54, 0) 0%, rgba(6, 4, 54, 0.8) 24.84%, #060436 50.43%, rgba(6, 4, 54, 0.8) 77.4%, rgba(6, 4, 54, 0) 100%)',
            }}
          >
            <div className="text-[128px] text-white">
              {' '}
              <span
                className="min-w-[66px]"
                style={{
                  background: 'linear-gradient(180deg, #FFFFFF 0%, #FFE184 100%)',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {score.first}
              </span>{' '}
              <span
                className="min-w-[66px]"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(255, 255, 255, 1) 0%, rgba(79, 181, 255, 1) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                :{' '}
                <span>
                  {roundWonVisible
                    ? score.second
                    : lastRoundOutcome === RoundOutcome.Draw
                      ? score.second
                      : score.second - 1}
                </span>
              </span>
            </div>

            <p
              className="flex items-center justify-center text-center text-[32px] font-bold leading-[40px] transition-opacity duration-500 ease-out"
              style={{
                background:
                  lastRoundOutcome === RoundOutcome.Draw
                    ? 'linear-gradient(180deg, rgba(255, 255, 255, 1) 0%, rgba(79, 181, 255, 1) 100%)'
                    : 'linear-gradient(180deg, #FFFFFF 0%, #FF7E8E 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                flex: 'none',
                order: 1,
                flexGrow: 0,
                opacity: roundWonVisible ? 1 : 0,
              }}
            >
              {lastRoundOutcome === RoundOutcome.Draw ? (
                <span>{textForDraw || t('cardAnimationRoundTexts.draw')}</span>
              ) : (
                <span>{textForLose || t('cardAnimationRoundTexts.lose')}</span>
              )}
            </p>
          </div>
        </Transition>
        <OptimizedImage
          imageData={
            showResultCards && lastRoundOutcome !== RoundOutcome.Draw ? myCard?.lose : myCard?.card
          }
          alt="card front"
          className={`z-5 h-[70vh] max-h-[390px] w-auto rotate-[15deg] transition-opacity transition-transform duration-300 ease-out ${secondCardVisible ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'} `}
          style={{
            marginTop: 'max(-21vh, -117px)', // 30% overlap: use smaller of 21vh or 117px
          }}
        />

        <Transition
          as={Fragment}
          show={showProgress}
          enter="transition-opacity duration-300 ease-out"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute bottom-10 w-[90%] max-w-72">
            <InfiniteMaxProgressBar value={Number(displayedPoints)} showAttention />
          </div>
        </Transition>
      </div>
    );
  },
);
